import { UseFormReturn } from 'react-hook-form';
import { Grid, Paper, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TextInput from '../../../forms/TextInput';
import SelectMenu from '../../../forms/SelectMenu';
import AddressInput from '../../../forms/AddressInput';
import { Shape } from '../../../../data/models/named-location.model';
import { ForUseAs } from '../../../../data/models/client-location.model';

export const ShapeOptions = [{ value: Shape.circle, label: 'Circle' }];
export const ForUseAsOptions = [
  { value: ForUseAs.approved, label: 'Approved' },
  { value: ForUseAs.restricted, label: 'Restricted' },
];

const PREFIX = 'ClientLocationForm';

export const classes = {
  container: `${PREFIX}-container`,
  fullWidth: `${PREFIX}-fullWidth`,
};

export const StyledBox = styled('div')(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },

  [`& .${classes.fullWidth}`]: {
    width: '100%',
  },
}));

interface ClientLocationFormProps {
  formHook: UseFormReturn<any, any>;
  isEdit?: boolean;
}

export default function ClientLocationForm({
  formHook,
  isEdit,
}: ClientLocationFormProps) {
  const { t } = useTranslation('common');
  const { control, formState, setValue, watch } = formHook;

  return (
    <StyledBox className={classes.container}>
      <TextInput
        label={t('namedLocations.name')}
        name="name"
        required={true}
        control={control}
        errors={formState.errors}
        rules={{
          required: t('common.requiredField'),
        }}
      />
      <TextInput
        label={t('client.description')}
        name="description"
        required={true}
        control={control}
        disabled={isEdit}
        errors={formState.errors}
        rules={{
          required: t('common.requiredField'),
        }}
      />
      <TextInput
        label={t('namedLocations.reason')}
        name="reason"
        required={true}
        control={control}
        errors={formState.errors}
        rules={{
          required: t('common.requiredField'),
        }}
      />
      <AddressInput
        name="address"
        label={t('namedLocations.address')}
        required={true}
        watch={watch}
        setValue={setValue}
      />
      <fieldset>
        <legend>{t('namedLocations.boundary')}</legend>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <SelectMenu
              label={t('namedLocations.shape')}
              name="boundary.shape"
              values={ShapeOptions}
              valueAttr="value"
              labelAttr="label"
              required={true}
              control={control}
              rules={{ required: t('common.requiredField') }}
              className={classes.fullWidth}
            />
          </Grid>
          <Grid item xs={6}>
            <TextInput
              label={t('namedLocations.radius')}
              name="boundary.radius"
              type="number"
              inputProps={{ min: 1, max: 610 }}
              required={true}
              control={control}
              errors={formState.errors}
              rules={{
                required: t('common.requiredField'),
              }}
              className={classes.fullWidth}
            />
          </Grid>
        </Grid>
      </fieldset>
      <SelectMenu
        label={t('client.forUseAs')}
        name="forUseAs"
        values={ForUseAsOptions}
        valueAttr="value"
        labelAttr="label"
        required={true}
        control={control}
        disabled={isEdit}
        rules={{ required: t('common.requiredField') }}
      />
    </StyledBox>
  );
}
